import * as React from 'react';
import {connect} from 'react-redux';

import moment from 'moment';

import {ActivityTypes} from 'nutshell-core/activity-types';
import {Assignables} from 'nutshell-core/assignables';
import {safelyGetFilterByKey} from 'nutshell-core/filter';
import * as Schema from 'nutshell-core/schema';
import type {FilterObject, Assignable, ActivityType} from 'nutshell-core/types';

import {Badge} from 'shells/badge/badge';
import {Button} from 'shells/button/button';
import {CogButton} from 'shells/button/cog-button/cog-button';
import {Checkbox} from 'shells/checkbox/checkbox';
import {DateRangePicker} from 'shells/date-range-picker/date-range-picker';
import {convertToModels} from 'shells/filter-capsule/convert-to-models';
import {FilterCapsule} from 'shells/filter-capsule/filter-capsule';
// @ts-expect-error - imported from .js/.jsx file
import {CloseIcon} from 'shells/icon/svg/close-icon';
import {useResizeObserver} from 'shells/utils/hooks/use-resize-observer';

import {analyticsClient} from '../../analytics-client';
import type {NutshellState} from '../../store';

import {useGetSessionUser} from '../core/use-get-session-user';
// @ts-expect-error
import {fetchDashboardSchema} from '../master-dashboard/dashboard-actions';
// @ts-expect-error
import * as dashboardSelectors from '../master-dashboard/dashboard-selectors';
import {useGenerateEntitySummary} from '../power-ai/timeline-summary//graphql/hooks/use-generate-entity-summary';
import {useGetEntitySummary} from '../power-ai/timeline-summary/graphql/hooks/use-get-entity-summary';
import {SummaryGenerateButton} from '../power-ai/timeline-summary/summary-generate-button';

import {getDateOptions, getMinDate} from './helpers';

import styles from './timeline-filter-bar.module.css';

export const TIMELINE_FILTER_BAR_CONTAINER_ID = 'timeline-filter-bar-container';

type OwnProps = {
    activeFilters: FilterObject[];
    onUpdateFilters: (filter: FilterObject, filterName: string) => void;
    onRemoveAllFilters: () => void;
    entityCreatedTime: string;
    excludeOutreach: boolean;
    excludeInteractions: boolean;
    excludeCustomerJourneys: boolean;
    setExcludeOutreach: () => Promise<any>;
    setExcludeInteractions: () => Promise<any>;
    setExcludeCustomerJourneys: () => Promise<any>;
    allowFilteringOnLeadUpdates: boolean;
    isViewingChangelog: boolean;
    setIsViewingChangelog: (arg1: boolean) => void;
    entityId: string;
    shouldShowSummarizeButton?: boolean;
    hasCalculatedWidth?: boolean;
};

type DispatchProps = {
    getDashboardSchema: () => void;
};

type StateProps = {
    schema: Schema.Properties;
    assignables: Assignable[];
    activityTypes: ActivityType[];
};

type Props = OwnProps & StateProps & DispatchProps;

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        getDashboardSchema() {
            dispatch(fetchDashboardSchema());
        },
    };
}

function mapStateToProps(state: NutshellState): StateProps {
    return {
        schema: dashboardSelectors.getDashboardSchema(state),
        assignables: Assignables.getAll(state),
        activityTypes: ActivityTypes.getAllLoggable(state),
    };
}

export function TimelineFilterBarComponent(props: Props) {
    const {
        schema,
        assignables,
        activityTypes,
        activeFilters,
        onUpdateFilters,
        onRemoveAllFilters,
        entityCreatedTime,
        excludeOutreach,
        excludeInteractions,
        excludeCustomerJourneys,
        setExcludeOutreach,
        setExcludeInteractions,
        setExcludeCustomerJourneys,
        allowFilteringOnLeadUpdates,
        isViewingChangelog,
        setIsViewingChangelog,
        hasCalculatedWidth = true,
    } = props;
    const [isShowingMoreOptions, setIsShowingMoreOptions] = React.useState<boolean>(false);
    const [isDateFilterHovered, setIsDateFilterHovered] = React.useState<boolean>(false);
    // State to maintain if filter bar container is currently in view on the page. If it is not,
    // we will render the filter bar in a fixed position outside the container, otherwise, the
    // filter bar will render normally within the container.
    const [isContainerInView, setIsContainerInView] = React.useState<boolean>(true);
    const filterBarContainerRef = React.useRef();
    const [width] = useResizeObserver(filterBarContainerRef.current);
    const {user} = useGetSessionUser();
    const {entitySummary, isLoading, isRefetching} = useGetEntitySummary(
        props.entityId,
        'TIMELINE'
    );
    const {generateEntitySummary, isLoading: isGeneratingSummary} = useGenerateEntitySummary();

    const shouldShowSummarizeButton =
        props.shouldShowSummarizeButton &&
        !isGeneratingSummary &&
        !isRefetching &&
        !isLoading &&
        !entitySummary;

    React.useEffect(() => {
        props.getDashboardSchema();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGenerateSummary = () => {
        generateEntitySummary(
            {entityId: props.entityId, summaryType: 'TIMELINE'},
            entitySummary ? entitySummary.id : undefined
        );
    };

    const getPayloads = () => {
        const payloadEnum =
            Boolean(schema) && schema.payload && schema.payload.type === 'enum'
                ? schema.payload.enum
                : undefined;

        if (payloadEnum) {
            return allowFilteringOnLeadUpdates
                ? payloadEnum
                : // @ts-expect-error
                  payloadEnum.filter((p) => p.id !== 'leadUpdates');
        } else {
            return [];
        }
    };

    const payloadModels = convertToModels(getPayloads());
    const assignableModels = convertToModels(assignables);
    const activityTypeModels = convertToModels(activityTypes);

    // To clear an existing filter, we replace it with an empty object. This checks
    // that any activeFilters are actually valid, not ones that did exist but then were
    // removed.
    const validFilters = activeFilters.filter((f) => Object.keys(f).length !== 0);
    const isFiltering = Boolean(validFilters.length);

    React.useEffect(() => {
        const handleScroll = () => {
            if (filterBarContainerRef && filterBarContainerRef.current) {
                // @ts-expect-error
                setIsContainerInView(filterBarContainerRef.current.getBoundingClientRect().top > 2);
            }
        };

        // Add event listener for page scroll
        window.addEventListener('scroll', handleScroll);

        // Cleanup event listener
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const activeDateFilter = safelyGetFilterByKey(activeFilters, 'period');
    const exclusionCount = (() => {
        let count = 0;
        if (excludeInteractions) {
            count++;
        }
        if (excludeOutreach) {
            count++;
        }
        if (excludeCustomerJourneys) {
            count++;
        }

        return count;
    })();

    return (
        <div
            id={TIMELINE_FILTER_BAR_CONTAINER_ID}
            // @ts-expect-error
            ref={(ref) => (filterBarContainerRef.current = ref)}
            className={styles['filter-bar-container']}
        >
            <div
                // Give the filter bar a set width using the container width
                style={{width: hasCalculatedWidth ? width : undefined}}
                className={isContainerInView ? styles['filter-bar'] : styles['filter-bar--fixed']}
            >
                <div className={styles['filter-bar-controls']}>
                    <div className={styles['filters-container']}>
                        <div className={styles.filters}>
                            {props.isViewingChangelog ? undefined : (
                                <>
                                    <div className={styles['filter-capsule']}>
                                        <FilterCapsule
                                            key='payload'
                                            size='big'
                                            collection={payloadModels}
                                            filter={safelyGetFilterByKey(activeFilters, 'payload')}
                                            filterName='payload'
                                            hasSearch={false}
                                            defaultValueTitle='All entries'
                                            schemaField={schema.payload}
                                            onSubmit={onUpdateFilters}
                                            onRemove={(key) => onUpdateFilters({}, key)}
                                        />
                                    </div>
                                    <div className={styles['filter-capsule']}>
                                        <FilterCapsule
                                            key='activityType'
                                            size='big'
                                            collection={activityTypeModels}
                                            filter={safelyGetFilterByKey(
                                                activeFilters,
                                                'activityType'
                                            )}
                                            filterName='activityType'
                                            defaultValueTitle='All activity types'
                                            schemaField={schema.activityType}
                                            onSubmit={onUpdateFilters}
                                            onRemove={(key) => onUpdateFilters({}, key)}
                                        />
                                    </div>
                                    <div className={styles['filter-capsule']}>
                                        <FilterCapsule
                                            key='creator'
                                            size='big'
                                            collection={assignableModels}
                                            filter={safelyGetFilterByKey(activeFilters, 'creator')}
                                            filterName='creator'
                                            defaultValueTitle='All users & teams'
                                            schemaField={schema.creator}
                                            onSubmit={onUpdateFilters}
                                            onRemove={(key) => onUpdateFilters({}, key)}
                                        />
                                    </div>
                                    <div
                                        className='my-4'
                                        onMouseEnter={() => setIsDateFilterHovered(true)}
                                        onMouseLeave={() => setIsDateFilterHovered(false)}
                                    >
                                        <DateRangePicker
                                            isFilterCapsule={true}
                                            filter={activeDateFilter}
                                            onChange={(dateString: string) => {
                                                onUpdateFilters({period: dateString}, 'period');
                                                analyticsClient.track(
                                                    analyticsClient.EVENTS
                                                        .TRACK_TIMELINE_FILTER_DATE_RANGE
                                                );
                                            }}
                                            dateRangeOptions={getDateOptions(entityCreatedTime)}
                                            minDate={getMinDate(entityCreatedTime)}
                                            maxDate={moment()}
                                        />
                                        {activeDateFilter ? (
                                            <Badge
                                                contents={
                                                    isDateFilterHovered ? (
                                                        <CloseIcon
                                                            size={8}
                                                            fill='#fff'
                                                            wrapWithDiv={false}
                                                        />
                                                    ) : undefined
                                                }
                                                onClick={() => onUpdateFilters({}, 'period')}
                                                color='grey'
                                            />
                                        ) : undefined}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className={styles['buttons-container']}>
                            <div className={styles['more-button']}>
                                <CogButton
                                    onClick={() => setIsShowingMoreOptions(!isShowingMoreOptions)}
                                    size={24}
                                />
                                {exclusionCount ? (
                                    <Badge contents={exclusionCount} color='grey' />
                                ) : undefined}
                            </div>
                            {isFiltering ? (
                                <Button
                                    size='small'
                                    variant='text-primary'
                                    onClick={onRemoveAllFilters}
                                >
                                    Remove filters
                                </Button>
                            ) : undefined}
                        </div>
                    </div>
                    <div className={styles['buttons-container']}>
                        {shouldShowSummarizeButton && (
                            <SummaryGenerateButton
                                onGenerateEntitySummary={handleGenerateSummary}
                            />
                        )}
                        {user && user.permissions.canAccessSetup ? (
                            <Button
                                onClick={() => setIsViewingChangelog(!isViewingChangelog)}
                                variant={isViewingChangelog ? 'primary' : 'secondary'}
                                size='big'
                            >
                                {isViewingChangelog ? 'Go back to timeline' : 'View changelog'}
                            </Button>
                        ) : undefined}
                    </div>
                </div>
                {isShowingMoreOptions ? (
                    <div
                        style={{
                            width:
                                hasCalculatedWidth && width ? `calc(${width}px - 32px)` : undefined,
                        }}
                        className={styles['more-options']}
                    >
                        <div className={styles.info}>
                            Additional settings <span>(Selections will persist across pages)</span>
                        </div>
                        <div className={styles['checkbox-container']}>
                            <Checkbox
                                label='Hide marketing outreach'
                                checked={excludeOutreach}
                                onChange={setExcludeOutreach}
                            />
                            <Checkbox
                                label='Hide email views & clicks'
                                checked={excludeInteractions}
                                onChange={setExcludeInteractions}
                            />
                            {NutClientConfig.hasCustomerJourneys ? (
                                <Checkbox
                                    label='Hide website visits'
                                    checked={excludeCustomerJourneys}
                                    onChange={setExcludeCustomerJourneys}
                                />
                            ) : undefined}
                        </div>
                    </div>
                ) : undefined}
            </div>
        </div>
    );
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const TimelineFilterBar = connector(TimelineFilterBarComponent);
